import React, { useEffect, useCallback, useRef, useState } from 'react';
import Script from 'next/script';
import { useTranslation } from 'react-i18next';
import { useOnScreen } from '../../../hooks/useOnScreen';
import { Section, Card, Title, Subtitle, FormContainer } from './styled';

const HUB_ID = '144986859';

const FORM_IDS: Record<'az' | 'ru', string> = {
  az: 'b40f6bb5-7b12-4855-9ad2-86a1b10da34d',
  ru: 'a03f2759-7d33-4b3f-97d5-4b54586d9fb4'
};

type HsCreateOptions = {
  portalId: string;
  formId: string;
  region?: string;
  target: string;
  onFormSubmitted?: () => void;
  onFormReady?: (form?: HTMLFormElement) => void;
};

declare global {
  interface Window {
    hbspt?: { forms: { create: (opts: HsCreateOptions) => void } };
  }
}

interface Props {
  setFormRef?: (ref: React.RefObject<HTMLDivElement>) => void;
  setVisibleTabRef?: (ref: React.RefObject<HTMLDivElement>) => void;
}

export const SendRequestBlock: React.FC<Props> = ({
  setFormRef,
  setVisibleTabRef
}) => {
  const { t, i18n } = useTranslation();

  const [submitted, setSubmitted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(wrapperRef);

  const moRef = useRef<MutationObserver | null>(null);
  const boundFormsRef = useRef<WeakSet<Element>>(new WeakSet());

  useEffect(() => {
    setFormRef?.(wrapperRef);
    if (isVisible) setVisibleTabRef?.(wrapperRef);
  }, [isVisible, setFormRef, setVisibleTabRef]);

  const getFormId = useCallback(
    () => (i18n.language === 'ru' ? FORM_IDS.ru : FORM_IDS.az),
    [i18n.language]
  );

  const initFloatingLabels = useCallback((root: HTMLElement) => {
    const form =
      (root.querySelector('form.hs-form') as HTMLFormElement | null) ||
      (root.closest('form.hs-form') as HTMLFormElement | null);

    if (!form) return;
    if (boundFormsRef.current.has(form)) return;
    boundFormsRef.current.add(form);

    const FIELD = '.hs-form-field';
    const INPUTS =
      'input.hs-input, select.hs-input, textarea.hs-input, input, select, textarea';

    const update = (el: Element) => {
      const field = el.closest(FIELD) as HTMLElement | null;
      if (!field) return;
      const raw = (el as HTMLInputElement).value ?? '';
      const v = typeof raw === 'string' ? raw.trim() : '';
      field.classList.toggle('has-value', v.length > 0);
    };

    const refresh = () => {
      form.querySelectorAll<HTMLInputElement>(INPUTS).forEach(update);
    };

    const handler = (e: Event) => {
      const targetEl = e.target as Element;
      if (targetEl?.matches?.(INPUTS)) update(targetEl);
    };

    form.addEventListener('input', handler, true);
    form.addEventListener('change', handler, true);
    form.addEventListener('blur', handler, true);

    const mo = new MutationObserver(() => refresh());
    mo.observe(form, { childList: true, subtree: true });
    moRef.current = mo;

    refresh();
    requestAnimationFrame(refresh);
    setTimeout(refresh, 100);
    setTimeout(refresh, 500);
    setTimeout(refresh, 1500);
  }, []);

  const mountForm = useCallback(() => {
    if (!window.hbspt) return;
    const target = document.getElementById('hubspotForm');
    if (!target) return;

    target.innerHTML = '';
    moRef.current?.disconnect();
    moRef.current = null;
    boundFormsRef.current = new WeakSet();

    window.hbspt.forms.create({
      portalId: HUB_ID,
      formId: getFormId(),
      region: 'eu1',
      target: '#hubspotForm',
      onFormSubmitted: () => setSubmitted(true),
      onFormReady: () => {
        const container = document.getElementById('hubspotForm');
        if (container) initFloatingLabels(container);
      }
    });
  }, [getFormId, initFloatingLabels]);

  useEffect(() => {
    if (window.hbspt) mountForm();
    return () => moRef.current?.disconnect();
  }, [mountForm]);

  return (
    <Section ref={wrapperRef}>
      <Script
        id="hs-forms-v2"
        src="https://js-eu1.hsforms.net/forms/embed/v2.js"
        strategy="lazyOnload"
        onLoad={mountForm}
      />

      <Card>
        {!submitted && (
          <>
            <Title>{t('pasha.request_form.title')}</Title>
            <Subtitle>{t('pasha.request_form.subtitle')}</Subtitle>
          </>
        )}
        <FormContainer id="hubspotForm" />
      </Card>
    </Section>
  );
};
